import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { api_url } from "../config/config";
import HeroBannner from "./HeroBannner";
import manufacturer_default from "../images/prodimages/manufacturer_default.svg";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

/* ── Safe slug ── */
const toSlug = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

/* ── Fallback SVG ── */
const BrandFallback = () => (
  <img src={manufacturer_default} alt="default" className="" />
);

/* ── Brand Card ── */
const BrandCard = ({ item, index, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const hasImage = item.src && !imgError;
  const isOrange = index % 2 === 1;
  const cardBg = isOrange
    ? "bg-gradient-to-b from-[#fff3ee] to-white"
    : "bg-gradient-to-b from-[#eaf4f3] to-white";
  const cardBorder = isOrange
    ? "border-[#ffd5c2] hover:border-[#ff601e]/60"
    : "border-[#c5d5d4] hover:border-[#5fbdb3]";
  const accentGrad = isOrange
    ? "from-[#ff601e] to-[#e55519]"
    : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
  const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
  const nameCls = isOrange
    ? "text-[#c44a14] group-hover:text-[#ff601e]"
    : "text-[#1e4a46] group-hover:text-[#3a7a74]";
  const stripBg = isOrange
    ? "bg-[#fff8f5]/90 border-[#ffd5c2]/40"
    : "bg-[#f4fbfa]/90 border-[#d4ecea]/40";
  const dotCls = isOrange ? "bg-[#ff601e]" : "bg-[#5fbdb3]";
  const shadow = isOrange
    ? "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]"
    : "shadow-[0_2px_12px_rgba(95,189,179,0.10)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]";

  return (
    <div
      onClick={onClick}
      className={`group border-[1.5px] ${cardBorder} ${cardBg} ${shadow}
        rounded-2xl overflow-hidden flex flex-col cursor-pointer relative
        hover:-translate-y-2 transition-all duration-300`}
      style={{ animation: `fadeInUp 0.35s ease ${(index % 12) * 35}ms both` }}
    >
      <div
        className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentGrad} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
      />
      <div
        className={`absolute top-2.5 right-2.5 w-2 h-2 rounded-full ${dotCls} opacity-20 group-hover:opacity-65 group-hover:scale-125 transition-all duration-300 z-10`}
      />

      <div
        className={`w-full h-[220px] ${imgBg}  flex items-center justify-center  flex-shrink-0`}
      >
        {hasImage ? (
          <img
            src={item.src}
            alt={item.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full ">
            <BrandFallback />
          </div>
        )}
      </div>

      <div className="w-full h-px bg-black/[0.05]" />

      <div
        className={`px-3 py-2.5 flex items-center justify-between gap-2 border-t ${stripBg}`}
      >
        <p
          className={`text-[11px] sm:text-[12px] font-semibold leading-snug line-clamp-1 flex-1 transition-colors duration-200 ${nameCls}`}
        >
          {item.name || "Brand"}
        </p>
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5fbdb3"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 -translate-x-1.5 group-hover:translate-x-0 transition-all duration-200"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

/* ── Hero icon ── */
const BrandsIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#5fbdb3"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

/* ════════════════════════════════════════
   PAGE
════════════════════════════════════════ */
const ShopBrand = () => {
  const navigate = useNavigate();
  const [manufacturers, setManufacturers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [totalManufacturers, setTotalManufacturers] = useState(0);
  const [itemsPerPage] = useState(18);
  const searchInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  /* ── Fetch manufacturers with search and pagination ── */
  const fetchManufacturers = () => {
    setIsLoading(true);

    // Build query parameters
    const params = new URLSearchParams({
      page: currentPage,
      limit: itemsPerPage,
    });

    // Add search parameter if search term exists
    if (debouncedSearchTerm.trim()) {
      params.append('search', debouncedSearchTerm.trim());
    }

    axios
      .get(`${api_url}/manufacturer?${params.toString()}`)
      .then((response) => {
        if (response.data?.results) {
          const formatted = response.data.results
            .filter((m) => m && m.manufacturer_id)
            .map((m) => ({
              src: m.image_url,
              name: m.manufacturer_name || m.company_name || "",
              manufacturer_id: m.manufacturer_id,
            }));

          setManufacturers(formatted);
          setTotalPages(response.data.totalPages || Math.ceil((response.data.total || response.data.totalDocuments || 0) / itemsPerPage));
          setTotalManufacturers(response.data.total || response.data.totalDocuments || 0);
        }
      })
      .catch((err) => console.error("Error fetching manufacturers:", err))
      .finally(() => setIsLoading(false));
  };

  /* ── Fetch on page change or search term change ── */
  useEffect(() => {
    fetchManufacturers();
  }, [currentPage, debouncedSearchTerm]);

  /* ── Search handler ── */
  const handleSearch = (val) => {
    setSearchTerm(val);
    setCurrentPage(1); // Reset to first page when searching
  };

  /* ── View all ── */
  const handleViewAll = async () => {
    setIsLoading(true);
    setSearchTerm("");
    setCurrentPage(1);

    try {
      const res = await axios.get(`${api_url}/manufacturer?page=1&limit=200`);

      if (res.data?.results) {
        const formatted = res.data.results
          .filter((m) => m && m.manufacturer_id)
          .map((m) => ({
            src: m.image_url,
            name: m.manufacturer_name || m.company_name || "",
            manufacturer_id: m.manufacturer_id,
          }));

        setManufacturers(formatted);
        setTotalPages(1);
        setTotalManufacturers(formatted.length);
      }
    } catch (err) {
      console.error("Error fetching all manufacturers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Navigate ── */
  const handleManufect = (manufacturer) => {
    navigate(`/products`, {
      state: {
        preselectedBrand: {
          id: manufacturer.manufacturer_id,
          name: manufacturer.name,
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins overflow-x-hidden">
      <Helmet>
        <title>All Brands | Online Medical Store | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="MediSYS Pharmacy - Buy medicines online and experience fastest home delivery."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* ── Global Hero ── */}
      <HeroBannner
        breadcrumbs={[
          { label: "Home", to: "/home" },
          { label: "Shop by Brands" },
        ]}
        title="Shop by Brands"
        subtitle="Browse medicines from your trusted pharmaceutical brands - all verified and authentic."
        badge="Explore"
        icon={<BrandsIcon />}
        stat={
          !isLoading
            ? { value: `${totalManufacturers}+`, label: "Verified Brands" }
            : null
        }
        accentColor="#5fbdb3"
      />

      {/* ── Search + filter bar ── */}
      <div className="bg-white border-b border-[#e2eaea] px-4 sm:px-8 lg:px-14 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2.5 flex-1 bg-[#f8fafa] border border-[#e2eaea] focus-within:border-[#5fbdb3] focus-within:bg-white rounded-xl px-4 h-10 transition-all duration-200">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8faba9"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search brands…"
              className="flex-1 bg-transparent outline-none text-[13px] font-medium text-[#1e4a46] placeholder-[#8faba9] font-poppins"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  handleSearch("");
                  searchInputRef.current?.focus();
                }}
                className="text-[#8faba9] hover:text-[#1e4a46] transition-colors"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Right: count pill + view all */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {!isLoading && (
              <div className="flex items-center gap-1.5 bg-[#eaf4f3] border border-[#d4ecea] rounded-xl px-3 h-10">
                <span className="font-bold text-[13px] text-[#1e4a46]">
                  {manufacturers.length}
                </span>
                <span className="text-[12px] text-[#5fbdb3] font-medium">
                  brands
                </span>
              </div>
            )}
            {!isLoading && !searchTerm && totalPages > 1 && (
              <button
                onClick={handleViewAll}
                className="flex items-center gap-1.5 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[13px] px-4 h-10 rounded-xl transition-all duration-200 whitespace-nowrap"
              >
                View All
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-4 sm:px-8 lg:px-14 py-8">
        {/* Section heading */}
        <div className="flex items-center gap-4 mb-6">
          <h2 className="font-bold text-[16px] sm:text-[18px] text-[#1e4a46]">
            {searchTerm ? `Results for "${searchTerm}"` : "All Brands"}
          </h2>
          <div className="flex-1 h-px bg-[#e2eaea]" />
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#e2eaea] overflow-hidden animate-pulse flex flex-col"
              >
                <div className="h-[220px] bg-[#e2eaea]" />
                <div className="p-3 flex gap-2 items-center">
                  <div className="h-3 bg-[#e2eaea] rounded-full flex-1" />
                </div>
              </div>
            ))}
          </div>
        ) : manufacturers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {manufacturers.map((item, index) => (
              <BrandCard
                key={item.manufacturer_id}
                item={item}
                index={index}
                onClick={() => handleManufect(item)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#5fbdb3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <p className="font-semibold text-[15px] text-[#374747]">
              No brands found
            </p>
            <p className="text-[13px] text-[#8faba9]">
              {searchTerm
                ? "Try a different search term."
                : "No brands available at the moment."}
            </p>
            {searchTerm && (
              <button
                onClick={() => handleSearch("")}
                className="flex items-center gap-2 bg-[#1e4a46] hover:bg-[#162c2a] text-white font-semibold text-[13px] px-5 h-9 rounded-xl transition-all duration-200"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Pagination - only show when not searching and multiple pages exist */}
        {!isLoading && totalPages > 1 && !searchTerm && (
          <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span
                    key={`dot-${idx}`}
                    className="text-[#8faba9] text-[13px] px-1"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`w-9 h-9 rounded-xl border-[1.5px] text-[13px] font-semibold flex items-center justify-center transition-all ${currentPage === item ? "bg-[#1e4a46] border-[#1e4a46] text-white shadow-[0_2px_8px_rgba(22,44,42,0.25)]" : "border-[#e2eaea] bg-white text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3]"}`}
                  >
                    {item}
                  </button>
                ),
              )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Pagination for search results - if backend supports it */}
        {!isLoading && totalPages > 1 && searchTerm && (
          <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span
                    key={`dot-${idx}`}
                    className="text-[#8faba9] text-[13px] px-1"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`w-9 h-9 rounded-xl border-[1.5px] text-[13px] font-semibold flex items-center justify-center transition-all ${currentPage === item ? "bg-[#1e4a46] border-[#1e4a46] text-white shadow-[0_2px_8px_rgba(22,44,42,0.25)]" : "border-[#e2eaea] bg-white text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3]"}`}
                  >
                    {item}
                  </button>
                ),
              )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center hover:bg-[#eaf4f3] hover:border-[#5fbdb3] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ShopBrand;